import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import Footer from "../Footer";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import _ from "lodash";
import UserContext from "../../utils/Contexts/UserContext";
import axiosInstance from "../../utils/Requests/axios";
import toast from "react-hot-toast";
import ExportWebsiteModal from "../../modals/ExportWebsiteModal";
import ExportTable from "./ExportTable";
import TablePagination from "../Common/Pagination";

const Exports = () => {
  const { t } = useContext(LanguageContext);
  const { userData } = useContext(UserContext);
  const [pageCount, setPageCount] = useState(15);
  const [activePage, setActivePage] = useState(1);
  const [columns, setColumns] = useState({
    date: true,
    product: true,
    type: true,
    items_status: true,
    status: true,
    items: true,
    file_csv: true,
  });

  const handleToggle = (column) => {
    setColumns((prevColumns) => ({
      ...prevColumns,
      [column]: !prevColumns[column],
    }));
  };

  const [columnSorts, setColumnSorts] = useState({
    0: "desc",
    1: null,
    2: null,
    3: null,
    4: null,
    5: null,
  });
  const [tableData, setTableData] = useState(null);
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [shouldUpdate, setShouldUpdate] = useState(false);
  const [formData, setFormData] = useState({
    draw: 1,
    columns: [
      { data: "created" },
      { data: "product" },
      { data: "type" },
      { data: "items-status" },
      { data: "status" },
      { data: "items" },
    ],
    order: [{ column: 0, dir: "desc" }],
    start: 0,
    length: 15,
    search: {
      value: "",
      filters: [],
    },
  });

  useEffect(() => {
    const payload = { ...formData };
    const start = activePage === 1 ? 0 : (activePage - 1) * pageCount;
    if (payload.start !== start || payload.length !== pageCount) {
      payload.start = start;
      payload.length = pageCount;
      setFormData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, pageCount]);

  const fetchWebsites = () => {
    const payload = _.cloneDeep(formData);
    const url = `users/${userData?.uuid}/export/search`;
    axiosInstance
      .post(url, payload)
      .then((response) => {
        const data = response?.data?.data || [];
        const filtered = response?.data?.recordsFiltered || 0;
        setTableData(data);
        setRecordsFiltered(filtered);
      })
      .catch(() => {
        toast.error(t("There was an error fetching backlinks offers"));
      });
  };

  useEffect(() => {
    const order = Object.keys(columnSorts)
      .filter((columnIndex) => columnSorts[columnIndex])
      .map((columnIndex) => ({
        column: Number(columnIndex),
        dir: columnSorts[columnIndex],
      }));

    if (!_.isEqual(formData.order, order)) {
      setFormData((prev) => ({
        ...prev,
        order,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSorts]);

  const previousPayloadRef = useRef(null);
  useEffect(() => {
    const payload = { ...formData };
    if (!_.isEqual(previousPayloadRef.current, payload)) {
      previousPayloadRef.current = payload;
      fetchWebsites();
    } else if (shouldUpdate) {
      setShouldUpdate(false);
      fetchWebsites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, shouldUpdate]);

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      search: {
        ...prevData.search,
        value: searchTerm,
      },
    }));
  }, [searchTerm]);

  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    _.debounce((searchTerm) => {
      setActivePage(1);
      setSearchTerm(searchTerm);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(search);
    return () => {
      debouncedSearch.cancel();
    };
  }, [search, debouncedSearch]);

  return (
    <main id="content" role="main" className="main">
      <div className="content container-fluid">
        <div
          className="page-header d-flex justify-content-between align-items-center"
          style={{ paddingBottom: "1em" }}
        >
          <div className="row align-items-center mb-3">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb breadcrumb-no-gutter">
                <li className="breadcrumb-item">
                  <a className="breadcrumb-link" href="/websites">
                    {t("Websites")}
                  </a>
                </li>
              </ol>
            </nav>

            <div className="col-md d-md-flex justify-content-between align-items-center mb-sm-2 mb-2 mb-md-0">
              <h1 className="page-header-title">{t("Your Exports")}</h1>
            </div>
          </div>
          <button
            className="btn btn-primary btn-sm d-flex justify-content-center align-items-center gap-2"
            style={{ borderRadius: "10px", width: "117px", height: "47px" }}
            data-bs-toggle="modal"
            data-bs-target="#exportModal"
          >
            <i className="bi-upload me-1"></i> {t("Export")}
          </button>
        </div>
        <div className="card">
          <div className="card-header d-flex justify-content-sm-between flex-row gap-2">
            <div className="search-input-container">
              <div className="input-group input-group-merge input-group-flush border rounded-2 pe-2">
                <div className="input-group-prepend input-group-text px-2">
                  <i className="bi-search"></i>
                </div>
                <input
                  id="datatableSearch"
                  type="search"
                  className="form-control custom-placeholder"
                  placeholder={t("Search Exports")}
                  aria-label="Search Exports"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="d-grid d-flex justify-content-end align-items-center gap-2">
              <div className="dropdown">
                <button
                  type="button"
                  className="btn btn-white w-100 text-nowrap text-secondary"
                  id="showHideDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  data-bs-auto-close="outside"
                >
                  <i className="bi bi-layout-three-columns me-1 text-secondary"></i>{" "}
                  {t("Columns")}
                </button>

                <div
                  className="dropdown-menu dropdown-menu-end dropdown-card"
                  aria-labelledby="showHideDropdown"
                  style={{ width: "15rem" }}
                >
                  <div className="card card-sm">
                    <div className="card-body">
                      <div className="d-grid gap-3">
                        {Object.keys(columns).map((key) => (
                          <label
                            key={key}
                            className="row form-check form-switch"
                            htmlFor={`toggleColumn_${key}`}
                          >
                            <span className="col-8 col-sm-9 ms-0">
                              <span className="me-2">
                                {t(_.startCase(key))}
                              </span>
                            </span>
                            <span className="col-4 col-sm-3 text-end">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`toggleColumn_${key}`}
                                checked={columns[key]}
                                onChange={() => handleToggle(key)}
                              />
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ExportTable
            data={tableData}
            columnsVisibility={columns}
            setColumnSorts={setColumnSorts}
            columnSorts={columnSorts}
          />

          <TablePagination
            activePage={activePage}
            setActivePage={setActivePage}
            recordsFiltered={recordsFiltered}
            setPageCount={setPageCount}
            pageCount={pageCount}
          />
        </div>
      </div>
      <ExportWebsiteModal setShouldUpdate={setShouldUpdate} />
      <Footer />
    </main>
  );
};

export default Exports;
